/** Matches football home carousel display (~460px wide). */
export const ADVERTISEMENT_BANNER_WIDTH = 920;
export const ADVERTISEMENT_BANNER_HEIGHT = 300;
export const ADVERTISEMENT_BANNER_MIN_WIDTH = 460;
export const ADVERTISEMENT_BANNER_MIN_HEIGHT = 250;
export const HOME_CHOOSER_BANNER_WIDTH = 1400;
export const HOME_CHOOSER_BANNER_HEIGHT = 920;
export const ADVERTISEMENT_BANNER_RATIO =
  ADVERTISEMENT_BANNER_WIDTH / ADVERTISEMENT_BANNER_HEIGHT;
export const ADVERTISEMENT_BANNER_RATIO_TOLERANCE = 0.1;
export const ADVERTISEMENT_BANNER_DISPLAY_HEIGHT = 320;

export function readImageFileDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

export function validateAdvertisementBannerDimensions(
  width,
  height,
  ratioTolerance = ADVERTISEMENT_BANNER_RATIO_TOLERANCE
) {
  if (!width || !height) {
    return "Could not read image dimensions.";
  }
  if (
    width < ADVERTISEMENT_BANNER_MIN_WIDTH ||
    height < ADVERTISEMENT_BANNER_MIN_HEIGHT
  ) {
    return `Banner must be at least ${ADVERTISEMENT_BANNER_MIN_WIDTH}×${ADVERTISEMENT_BANNER_MIN_HEIGHT} pixels (same size as football home banners).`;
  }
  const ratio = width / height;
  const delta =
    Math.abs(ratio - ADVERTISEMENT_BANNER_RATIO) / ADVERTISEMENT_BANNER_RATIO;
  if (delta > ratioTolerance) {
    return `Use a wide banner around ${ADVERTISEMENT_BANNER_WIDTH}×${ADVERTISEMENT_BANNER_HEIGHT} pixels so all competition ads display at the same size.`;
  }
  return null;
}

export async function resizeImageFileToCover(
  file,
  targetWidth = ADVERTISEMENT_BANNER_WIDTH,
  targetHeight = ADVERTISEMENT_BANNER_HEIGHT
) {
  const { width, height } = await readImageFileDimensions(file);
  if (!width || !height) throw new Error("Could not read image");
  const scale = Math.max(targetWidth / width, targetHeight / height);
  const dw = width * scale;
  const dh = height * scale;
  const dx = (targetWidth - dw) / 2;
  const dy = (targetHeight - dh) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not load image"));
      el.src = url;
    });
    ctx.fillStyle = "#242743";
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(img, dx, dy, dw, dh);
  } finally {
    URL.revokeObjectURL(url);
  }
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9)
  );
  if (!blob) throw new Error("Could not resize image");
  const base = (file.name || "banner").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
}

export async function validateAdvertisementBannerFile(file) {
  try {
    const { width, height } = await readImageFileDimensions(file);
    return validateAdvertisementBannerDimensions(width, height);
  } catch {
    return "Could not read image. Try another file.";
  }
}

export function readImageUrlDimensions(url, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error("Timed out loading image"));
    }, timeoutMs);

    const finish = (fn) => {
      clearTimeout(timer);
      fn();
    };

    img.onload = () => {
      finish(() => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      });
    };
    img.onerror = () => finish(() => reject(new Error("Could not load image")));
    img.src = url;
  });
}

export function adsWithBannerImages(ads = []) {
  return ads
    .filter((ad) => ad?.banner_image)
    .map((ad) => ({ ...ad, fitsSpec: false }));
}

/** Tag each ad for cover (spec fit) vs contain (legacy / unknown sizes). Always returns all ads. */
export async function prepareAdvertisementBannersForDisplay(ads = []) {
  const prepared = await Promise.all(
    ads.map(async (ad) => {
      if (!ad?.banner_image) return null;
      let fitsSpec = false;
      try {
        const { width, height } = await readImageUrlDimensions(ad.banner_image);
        fitsSpec = !validateAdvertisementBannerDimensions(width, height, 0.12);
      } catch {
        fitsSpec = false;
      }
      return { ...ad, fitsSpec };
    })
  );
  return prepared.filter(Boolean);
}
